// Profile picture cache management with localStorage and expiration handling

interface CachedProfilePic {
  url: string;
  timestamp: number;
  expiresAt?: number; // For AWS signed URLs
}

const CACHE_KEY_PREFIX = 'kick_pfp_';
const CLEANUP_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
const CACHE_TTL_MS = 24 * 60 * 60 * 1000;
const DEFAULT_AVATAR_CACHE_TTL_MS = 15 * 60 * 1000;
let lastCleanup = 0;
const inFlightProfilePics = new Map<string, Promise<string>>();
// Generate a consistent random avatar number for a username
function getDefaultAvatarForUser(userName: string): number {
  // Simple hash function to get consistent number for same username
  let hash = 0;
  for (let i = 0; i < userName.length; i++) {
    hash = ((hash << 5) - hash) + userName.charCodeAt(i);
    hash = hash & hash; // Convert to 32bit integer
  }
  return (Math.abs(hash) % 6) + 1; // Returns 1-6
}

// Get default avatar URL for a user
export function getDefaultAvatarUrl(userName: string): string {
  const avatarNum = getDefaultAvatarForUser(userName);
  return `https://kick.com/img/default-profile-pictures/default-avatar-${avatarNum}.webp`;
}


// Parse AWS signed URL to extract expiration
function parseAWSExpiration(url: string): number | null {
  try {
    const urlObj = new URL(url);
    const expiresParam = urlObj.searchParams.get('X-Amz-Expires');
    const dateParam = urlObj.searchParams.get('X-Amz-Date');
    
    if (expiresParam && dateParam) {
      // X-Amz-Date format: YYYYMMDDTHHMMSSZ
      const year = parseInt(dateParam.substring(0, 4));
      const month = parseInt(dateParam.substring(4, 6)) - 1;
      const day = parseInt(dateParam.substring(6, 8));
      const hour = parseInt(dateParam.substring(9, 11));
      const minute = parseInt(dateParam.substring(11, 13));
      const second = parseInt(dateParam.substring(13, 15));
      
      const signedDate = new Date(Date.UTC(year, month, day, hour, minute, second));
      const expiresInSeconds = parseInt(expiresParam);
      
      return signedDate.getTime() + (expiresInSeconds * 1000);
    }
  } catch (error) {
    console.error('Error parsing AWS URL expiration:', error);
  }
  return null;
}

// Check if cached profile pic is expired
function isCacheExpired(cached: CachedProfilePic): boolean {
  if (cached.expiresAt) {
    // AWS signed URL - check expiration
    return Date.now() >= cached.expiresAt;
  }
  const ttl = cached.url.includes('/default-profile-pictures/')
    ? DEFAULT_AVATAR_CACHE_TTL_MS
    : CACHE_TTL_MS;
  return !Number.isFinite(cached.timestamp) || Date.now() - cached.timestamp >= ttl;
}

function cleanupExpiredProfilePics(): void {
  try {
    const now = Date.now();
    if (now - lastCleanup < CLEANUP_INTERVAL_MS) return;
    lastCleanup = now;

    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key || !key.startsWith(CACHE_KEY_PREFIX)) continue;

      const raw = localStorage.getItem(key);
      if (!raw) continue;

      try {
        const cached: CachedProfilePic = JSON.parse(raw);
        if (isCacheExpired(cached)) {
          keysToRemove.push(key);
        }
      } catch {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.error('Error cleaning profile pic cache:', error);
  }
}

// Get cached profile pic from localStorage
export function getCachedProfilePic(userName: string): string | null {
  try {
    cleanupExpiredProfilePics();
    const cacheKey = CACHE_KEY_PREFIX + userName.toLowerCase();
    const cached = localStorage.getItem(cacheKey);
    
    if (!cached) return null;
    
    const cachedData: CachedProfilePic = JSON.parse(cached);
    
    // Check if expired
    if (isCacheExpired(cachedData)) {
      localStorage.removeItem(cacheKey);
      return null;
    }
    
    const mediumUrl = toMediumProfilePic(cachedData.url);
    if (mediumUrl && mediumUrl !== cachedData.url) {
      setCachedProfilePic(userName, mediumUrl);
      return mediumUrl;
    }

    return cachedData.url;
  } catch (error) {
    console.error('Error reading profile pic cache:', error);
    return null;
  }
}

// Save profile pic to localStorage
export function setCachedProfilePic(userName: string, url: string): void {
  try {
    const cacheKey = CACHE_KEY_PREFIX + userName.toLowerCase();
    const mediumUrl = toMediumProfilePic(url);
    const expiresAt = parseAWSExpiration(mediumUrl);
    
    const cacheData: CachedProfilePic = {
      url: mediumUrl,
      timestamp: Date.now(),
      expiresAt: expiresAt || undefined
    };
    
    localStorage.setItem(cacheKey, JSON.stringify(cacheData));
  } catch (error) {
    console.error('Error saving profile pic cache:', error);
  }
}

export function toMediumProfilePic(url: string): string {
  const raw = String(url || '').trim();
  if (!raw) return raw;

  try {
    const parsed = new URL(raw);
    if (parsed.pathname.includes('/default-profile-pictures/')) {
      return raw;
    }

    const isKickSignedS3Url =
      parsed.hostname.includes('kick-files-prod.s3') &&
      parsed.searchParams.has('X-Amz-Signature');

    if (isKickSignedS3Url) {
      const mediumPath = parsed.pathname.replace(/-[^/-]+\.webp$/i, '-medium.webp');
      return `https://files.kick.com${mediumPath}`;
    }

    if (parsed.hostname === 'files.kick.com' && /-[^/-]+\.webp$/i.test(parsed.pathname)) {
      parsed.pathname = parsed.pathname.replace(/-[^/-]+\.webp$/i, '-medium.webp');
      return parsed.toString();
    }
  } catch {
    return raw;
  }

  return raw;
}

// Fetch user profile pic from Kick API
async function fetchUserProfilePicOnce(userName: string): Promise<string> {
  cleanupExpiredProfilePics();
  // Check cache first
  const cached = getCachedProfilePic(userName);
  if (cached) {
    return cached;
  }

  try {
    const cleanedUserName = userName.trim().replace(/^@+/, '');
    const response = await fetch(`https://kick.com/api/v1/users/${encodeURIComponent(cleanedUserName)}`, {
      headers: {
        'accept': 'application/json, text/plain, */*',
        'accept-language': 'en-US,en;q=0.9',
        'user-agent': 'Mozilla/5.0 (Windows NT 10.0; Win64; x64) AppleWebKit/537.36',
        'referer': 'https://kick.com/',
        'origin': 'https://kick.com',
      },
    });
    
    if (!response.ok) {
      throw new Error(`Failed to fetch profile: ${response.status}`);
    }
    
    const data = await response.json();
    const profilePic = toMediumProfilePic(data?.profilepic || '');
    
    if (profilePic) {
      setCachedProfilePic(userName, profilePic);
      return profilePic;
    } else {
      // No profile pic, use default
      const defaultUrl = getDefaultAvatarUrl(userName);
      setCachedProfilePic(userName, defaultUrl);
      return defaultUrl;
    }
  } catch (error) {
    console.error(`Error fetching profile pic for ${userName}:`, error);
    // Return default avatar on error
    const defaultUrl = getDefaultAvatarUrl(userName);
    setCachedProfilePic(userName, defaultUrl);
    return defaultUrl;
  }
}

export async function fetchUserProfilePic(userName: string): Promise<string> {
  const key = userName.trim().replace(/^@+/, '').toLowerCase();
  const existing = inFlightProfilePics.get(key);
  if (existing) {
    return existing;
  }

  const request = fetchUserProfilePicOnce(userName);
  inFlightProfilePics.set(key, request);

  try {
    return await request;
  } finally {
    inFlightProfilePics.delete(key);
  }
}

// Clear all cached profile pics
export function clearProfilePicCache(): void {
  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (key && key.startsWith(CACHE_KEY_PREFIX)) {
        keysToRemove.push(key);
      }
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (error) {
    console.error('Error clearing profile pic cache:', error);
  }
}

// Lazy cleanup on module load (browser only)
try {
  if (typeof window !== 'undefined' && typeof localStorage !== 'undefined') {
    if ('requestIdleCallback' in window) {
      (window as any).requestIdleCallback(() => cleanupExpiredProfilePics());
    } else {
      setTimeout(() => cleanupExpiredProfilePics(), 0);
    }
  }
} catch {
  // ignore
}
